<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { Question } from "@/types/editor/Wizard";

const { question, questionNumber } = defineProps<{
  question: Question;
  questionNumber?: number;
}>();

const store = useStore();

const answers = computed(() => store.state.editor.wizard.answers);
const answer = computed({
  get: () => answers.value.find((a) => a.questionId === question.id)?.answer || null,
  set: (value) => {
    store.commit("editor/updateWizardAnswer", {
      questionId: question.id,
      answer: value,
    });
  }
});

const processedOptions = computed(() => {
  if (!question.options || typeof question.options !== 'object') {
    return [];
  }

  let options = [];

  // If options is already an array, process each item
  if (Array.isArray(question.options)) {
    options = question.options.map((option) => {
      return {
        value: option["@attributes"]?.value,
        label: option["@attributes"]?.label,
      };
    });
  } else {
    // If options is an object, process it
    options = Object.keys(question.options).map((key) => {
      return {
        value: question.options[key]["@attributes"]?.value,
        label: question.options[key]["@attributes"]?.label,
      };
    });
  }

  return options;
});
</script>

<template>
  <div class="document-question">
    <div class="question-header">
      <div class="question-number" v-if="questionNumber">
        {{ questionNumber }}
      </div>
      <div class="question-content">
        <h3 class="question-text">{{ question.content }}</h3>
      </div>
    </div>

    <div class="answer-section">
      <div class="answer-options">
        <div
          v-for="(option, index) in processedOptions"
          :key="option.value"
          class="option-item"
          :class="{ 'selected': answer === option.value }"
          @click="answer = option.value"
        >
          <div class="option-number" :class="{ 'selected': answer === option.value }">
            <v-icon v-if="answer === option.value" size="14" color="white">mdi-check</v-icon>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div class="option-content">
            <span class="option-label">{{ option.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.document-question {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 0.75rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: #CBD5E1;
  }
}

.question-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  .question-number {
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #3B82F6 0%, #1E3A8A 100%);
    color: white;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Archivo', sans-serif;
    font-weight: 600;
    font-size: 12px;
    flex-shrink: 0;
  }

  .question-content {
    flex: 1;

    .question-text {
      font-family: 'Archivo', sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 1.4;
      color: #1E293B;
      margin: 0;
      text-align: left;

      @media (max-width: 768px) {
        font-size: 15px;
      }
    }
  }
}

.answer-section {
  .answer-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

.option-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: white;
  border: 2px solid #E2E8F0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .option-number {
    width: 24px;
    height: 24px;
    background: #F1F5F9;
    border: 1px solid #CBD5E1;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Archivo', sans-serif;
    font-weight: 600;
    font-size: 12px;
    color: #64748B;
    flex-shrink: 0;
    transition: all 0.3s ease;

    &.selected {
      background: #0023EF;
      border-color: #0023EF;
      color: white;
    }
  }

  &:hover {
    border-color: #3B82F6;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }

  &.selected {
    border-color: #0023EF;
    background: linear-gradient(145deg, #F8FAFF 0%, #EEF4FF 100%);
    box-shadow: 0 4px 12px rgba(0, 35, 239, 0.15);

    .option-number {
      background: #0023EF;
      border-color: #0023EF;
      color: white;
    }

    .option-label {
      color: #0023EF;
      font-weight: 600;
    }
  }

  &.keyboard-focused {
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
    transform: translateY(-1px);
  }


  .option-content {
    flex: 1;

    .option-label {
      font-family: 'Archivo', sans-serif;
      font-weight: 500;
      font-size: 15px;
      line-height: 1.4;
      color: #374151;
      transition: all 0.3s ease;

      @media (max-width: 768px) {
        font-size: 14px;
      }
    }
  }
}

/* FORCE OVERRIDE ANY EXTERNAL CSS CAUSING BLUE BORDER */
.option-item:not(.selected) {
  border: 2px solid #E2E8F0 !important;
  border-color: #E2E8F0 !important;
  background: white !important;
}

.option-item.selected {
  border: 2px solid #0023EF !important;
  border-color: #0023EF !important;
  background: linear-gradient(145deg, #F8FAFF 0%, #EEF4FF 100%) !important;
}

/* FORCE OVERRIDE OPTION NUMBER STYLES */
.option-item:not(.selected) .option-number {
  background: #F1F5F9 !important;
  border: 1px solid #CBD5E1 !important;
  color: #64748B !important;
  box-shadow: none !important;
}

.option-item.selected .option-number {
  background: #0023EF !important;
  border: 1px solid #0023EF !important;
  color: white !important;
}

/* FORCE OVERRIDE KEYBOARD-FOCUSED CLASS */
.option-item.keyboard-focused:not(.selected) {
  border: 2px solid #E2E8F0 !important;
  border-color: #E2E8F0 !important;
  background: white !important;
  box-shadow: none !important;
  transform: none !important;
}

@media (max-width: 768px) {
  .document-question {
    padding: 0.75rem;
  }

  .question-header {
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .option-item {
    padding: 0.5rem;
    gap: 0.5rem;
  }
}
</style>